export default {
  "app": {
    "name": "DeepTrade",
    "tagline": "AI-Powered Cryptocurrency Trading",
    "description": "Advanced trading signals and automated portfolio management",
  },
  "navigation": {
    "dashboard": "Dashboard",
    "trading": "Trading",
    "signals": "Signals",
    "apiCredentials": "API Credentials",
    "autoTrading": "Auto Trading",
    "tierManagement": "Tier Management",
    "referrals": "Referrals",
    "aiConfig": "AI Config",
    "accessSecurity": "Access & Security",
    "settings": "Settings",
    "help": "Help & Support",
    "login": "Login",
    "register": "Register",
    "logout": "Logout",
    "home": "Home",
  },
  "auth": {
    "layout": {
      "signInToAccount": "Sign in to your account",
      "or": "Or",
      "createNewAccount": "create a new account"
    },
    "login": {
      "title": "Welcome Back",
      "subtitle": "Enter your email and password to sign in",
      "email": "Email",
      "password": "Password",
      "emailPlaceholder": "name@example.com",
      "passwordPlaceholder": "••••••••",
      "rememberMe": "Remember me",
      "forgotPassword": "Forgot password?",
      "signIn": "Sign In",
      "noAccount": "Don't have an account?",
      "signUp": "Sign up",
      "googleSignIn": "Continue with Google",
      "orContinueWith": "Or continue with",
      "fillAllFields": "Please fill in all fields",
      "loginSuccess": "Successfully logged in!",
      "loginFailed": "Login failed",
      "loginFailedDescription": "Failed to log in",
      "googleLoginFailed": "Failed to initiate Google login",
      "bySigningIn": "By signing in, you agree to our",
      "termsOfService": "Terms of Service",
      "privacyPolicy": "Privacy Policy",
      "and": "and"
    },
    "register": {
      "title": "Create Account",
      "subtitle": "Enter your information to create an account",
      "firstName": "First Name",
      "lastName": "Last Name",
      "email": "Email",
      "password": "Password",
      "confirmPassword": "Confirm Password",
      "agreeTerms": "By clicking continue, you agree to our",
      "createAccount": "Create account",
      "hasAccount": "Already have an account?",
      "signIn": "Sign in",
      "orContinueWith": "Or continue with",
      "googleSignUp": "Sign up with Google",
      "termsOfService": "Terms of Service",
      "privacyPolicy": "Privacy Policy",
      "referralCode": "Referral Code (Optional)",
      "placeholders": {
        "firstName": "John",
        "lastName": "Doe",
        "email": "name@example.com",
        "password": "Enter your password",
        "confirmPassword": "Confirm your password",
        "referralCode": "Enter referral code"
      },
      "validation": {
        "minLength": "Password must be at least 8 characters long",
        "uppercase": "Password must contain at least one uppercase letter",
        "lowercase": "Password must contain at least one lowercase letter",
        "number": "Password must contain at least one number",
        "specialChar": "Password must contain at least one special character"
      },
      "errors": {
        "invalidReferralCode": "Invalid referral code",
        "referralValidationFailed": "Unable to validate referral code. Please check your connection and try again.",
        "googleLoginFailed": "Failed to initiate Google login",
        "googleLoginFailedTitle": "Google login failed",
        "fillAllFields": "Please fill in all fields",
        "requirementsNotMet": "Password Requirements Not Met",
        "passwordsDoNotMatch": "Passwords do not match",
        "registrationFailed": "Registration failed",
        "failedToCreateAccount": "Failed to create account",
        "emailAlreadyRegistered": "Email already registered",
        "emailAlreadyRegisteredDescription": "An account with this email address already exists. Please try logging in instead.",
        "connectionError": "Connection Error",
        "connectionErrorDescription": "Unable to connect to the server. Please check your internet connection and try again.",
        "referralCodeError": "Referral Code Error",
        "referralCodeErrorDescription": "There was an issue processing your referral code. Your account will still be created.",
        "failedToResendEmail": "Failed to resend verification email",
        "failedToResendEmailTitle": "Failed to resend email",
        "tryAgainLater": "Please try again later."
      },
      "success": {
        "title": "Registration Successful!",
        "description": "Please check your email to verify your account."
      },
      "verification": {
        "title": "Check Your Email",
        "description": "We've sent a verification link to {{email}}",
        "nextSteps": "Next Steps:",
        "step1": "1. Check your email inbox",
        "step2": "2. Click the verification link",
        "step3": "3. Return here to log in",
        "sending": "Sending...",
        "resendButton": "Resend Verification Email",
        "backToLogin": "Back to Login",
        "emailSentTitle": "Verification Email Sent",
        "emailSentDescription": "Please check your email for the verification link."
      },
      "referral": {
        "validating": "Validating referral code...",
        "validCode": "Valid referral code from {{referrer}}"
      }
    },
    "passwordRequirements": {
      "title": "Password Requirements",
      "length": "At least 8 characters",
      "uppercase": "One uppercase letter",
      "lowercase": "One lowercase letter",
      "number": "One number",
      "special": "One special character",
    },
    "errors": {
      "invalidCredentials": "Invalid email or password",
      "emailRequired": "Email is required",
      "passwordRequired": "Password is required",
      "passwordTooShort": "Password must be at least 8 characters",
      "passwordsNotMatch": "Passwords do not match",
      "emailInvalid": "Please enter a valid email address",
      "termsRequired": "You must agree to the terms and conditions",
    }
  },
  "dashboard": {
    "title": "Dashboard",
    "welcome": "Welcome back!",
    "overview": "Overview",
    "trading": "Trading",
    "tier": "Tier",
    "balance": "Balance",
    "pnlToday": "Profit and Loss",
    "totalTrades": "Total Trades",
    "winRate": "Win Rate",
    "activeSignals": "Active Signals",
    "tradingOverview": "Trading Overview",
    "quickActions": "Quick Actions",
    "viewSignals": "View Signals",
    "autoTrading": "Auto Trading",
    "autoTradingDescription": "Automatically execute trading signals",
    "currentTier": "Current Tier",
    "progressTo": "Progress to Tier {{tier},",
    "yourBenefits": "Your Benefits",
    "advancedSignals": "Advanced Trading Signals",
    "autoTradingFeatures": "Auto-Trading Features",
    "prioritySupport": "Priority Support",
    "monthlyRate": "Monthly Rate",
    "currentMonthRate": "This Month",
    "monthlyAvgLabel": "Monthly Avg",
    "symbolSelector": "Symbol",
    "profitShare40": "40% profit share",
    "profitShare20": "20% profit share",
    "profitShare10": "10% profit share",
    "verifyingNFT": "Verifying NFT ownership...",
    "debt": "Debt",
    "btcPrice": {
      "symbol": "BTC/USDT",
      "loading": "BTC/USDT: Loading...",
      "unavailable": "BTC/USDT: --",
      "connecting": "Connecting...",
      "live": "Live",
      "error": "Error ({{count}})",
      "24h": "24h",
    },
    "controls": {
      "turnOn": "Turn ON",
      "turnOff": "Turn OFF",
      "paperAutoTrading": "Paper Auto-Trading",
    },
    "forecast": {
      "title": "Price Forecast",
      "refresh": "Refresh",
      "errorLoading": "Error loading chart",
      "retry": "Retry",
      "noData": "No chart data available",
    },
    "tradingChart": {
      "forecast": "Forecast",
      "upperBound": "Upper bound",
      "lowerBound": "Lower bound",
      "support": "Support",
      "resistance": "Resistance",
      "entry": "Entry",
      "stopLoss": "Stop loss",
      "takeProfit": "Take profit",
      "partialTakeProfit": "Partial TP",
      "liquidation": "Liquidation",
      "locked": "Locked",
      "positionLevelDialogTitle": "Confirm {{level}} update",
      "positionLevelDialogDescription": "Set {{level}} for {{symbol}} {{side}} from {{currentPrice}} to {{newPrice}}?",
      "positionLevelDelta": "Change: {{delta}}%",
      "positionLevelConfirm": "Update {{level}}",
      "positionLevelCancel": "Cancel",
      "positionLevelProcessing": "Updating...",
      "positionLevelSuccess": "{{level}} updated",
      "positionLevelSuccessDesc": "{{symbol}} {{side}} · new {{level}}: {{price}}",
      "positionLevelFailure": "Failed to update {{level}}",
      "positionLevelFailureDesc": "The previous level was restored. {{error}}",
      "dragHint": "Drag to move · release to confirm",
      "indicators": {
        "title": "Indicators",
        "overlays": "Overlays",
        "panes": "Panes",
        "levels": "Levels",
        "keyLevels": "Key levels (S/R)",
        "reset": "Reset indicators",
        "ema20": "EMA 20",
        "ema50": "EMA 50",
        "ema200": "EMA 200",
        "sma20": "SMA 20",
        "sma50": "SMA 50",
        "bb20": "Bollinger Bands (20, 2)",
        "bbUpper": "Bollinger upper",
        "bbMiddle": "Bollinger middle",
        "bbLower": "Bollinger lower",
        "vwap": "VWAP",
        "rsi14": "RSI (14)",
        "macd": "MACD (12, 26, 9)",
        "macdSignal": "MACD signal",
      },
      "drawing": {
        "toolbar": "Drawing tools",
        "select": "Select",
        "magnetMode": "Magnet: {{mode}}",
        "stayInDrawingMode": "Stay in drawing mode",
        "hideAll": "Hide all drawings",
        "showAll": "Show all drawings",
        "undo": "Undo",
        "redo": "Redo",
        "deleteSelected": "Delete selected drawings",
        "clearAll": "Clear all drawings",
        "moreTools": "More drawing tools",
        "export": "Export drawings",
        "import": "Import drawings",
        "textPrompt": "Enter drawing text",
        "loadFailure": "Drawing tools unavailable",
        "importEmpty": "No drawings found in this file",
        "importSuccess": "Imported {{count}} drawings",
        "importFailure": "Failed to import drawings",
        "clearConfirmTitle": "Clear all drawings?",
        "clearConfirmDescription": "This removes every drawing for the current symbol and timeframe. This cannot be undone.",
        "categories": {
          "lines": "Lines",
          "channels": "Channels",
          "fibonacci": "Fibonacci & Gann",
          "patterns": "Patterns",
          "elliott": "Elliott",
          "forecasting": "Forecasting & measurement",
          "volume": "Volume",
          "annotations": "Annotations",
          "shapes": "Shapes",
          "cycles": "Cycles",
          "regression": "Regression",
        },
        "tools": {
          "trend-line": "Trend line",
          "horizontal-line": "Horizontal line",
          "vertical-line": "Vertical line",
          "ray": "Ray",
          "parallel-channel": "Parallel channel",
          "fib-retracement": "Fib retracement",
          "trend-based-fib-extension": "Trend-based Fib extension",
          "rectangle": "Rectangle",
          "text": "Text",
          "arrow": "Arrow",
          "brush": "Brush",
          "price-range": "Price range",
          "long-position": "Long position",
          "short-position": "Short position",
        },
      },
    },
    "realTimePerformance": "Real-Time Performance",
    "riskSettings": {
      "title": "Risk Management Settings",
      "paperTradingTitle": "Risk Management Settings - Paper Trading",
      "loading": "Loading risk settings...",
      "riskControls": "Risk Controls",
      "strategy": "Strategy",
      "advancedOptions": "Advanced Options",
      "investmentPercentage": "Investment Percentage",
      "investmentDescription": "Percentage of balance to use for trading (0% - 10%)",
      "leverage": "Leverage",
      "leverageDescription": "Max: {{max}}x for Tier {{tier}} + {{exchange}} {{market}}",
      "paperTradingLeverageDesc": "In paper mode, you can use any leverage setting for testing purposes",
      "maxStopLoss": "Max Stop-Loss",
      "maxStopLossDesc": "Optional limit for maximum stop-loss in USD",
      "exchange": "Exchange: ",
      "configureCredentials": "Configure in API Credentials",
      "warningZeroInvestment": "Select investment percentage > 0% to enable trading",
      "paperTradingNote": "Paper trading mode - no tier limitations apply",
      "validationWarnings": "Validation Warnings",
      "suggestions": "Suggestions",
      "unableToValidate": "Unable to validate settings. Please check your API credentials.",
      "saveSettings": "Save Settings",
      "saving": "Saving...",
      "resetToSafe": "Reset to Safe",
      "confirmSaveTitle": "Confirm Risk Settings",
      "confirmSaveDesc": "Are you sure you want to save these risk settings?\n\nInvestment: {{investment}}%\nLeverage: {{leverage}}x\nMode: {{mode}}",
      "confirmSave": "Save Settings",
      "saveSuccess": "Settings Saved",
      "saveSuccessDesc": "Your risk management settings have been saved successfully.",
      "saveError": "Save Failed",
      "tradingStrategy": "Trading Strategy",
      "strategyActive": "Standard Strategy",
      "strategyConservative": "Neural AI Strategy",
      "strategyActiveBadge": "Recommended",
      "strategyActiveDesc": "More frequent trades based on ML price predictions. Generates 3-8 signals per day with 75-80% expected accuracy. Best for traders who want more opportunities and can handle moderate activity.",
      "strategyConservativeDesc": "Fewer, higher-confidence trades with strict confirmations. Generates 1-3 signals per day with 85-90% expected accuracy. Best for conservative traders who prefer quality over quantity.",
      "gridTradingTitle": "Grid Trading",
      "gridEnabledLabel": "Enable Grid Trading",
      "trailingStopTitle": "Trailing Stop",
      "trailingStopEnabledLabel": "Enable Trailing Stop",
      "tradingSymbolsLabel": "Trading Symbols",
      "tradingSymbolsHint": "Only checked symbols auto-trade",
      "tradingSymbolsEmptyWarning": "No symbols selected - auto-trading will not place trades for this account.",
      "gridLevelsLabel": "Number of Grid Levels",
      "gridSizingModeLabel": "Position Sizing Mode",
      "gridSizingEqual": "Equal",
      "gridSizingScaled": "Scaled",
      "gridScaleMultiplierLabel": "Scale Multiplier",
      "gridScaleMultiplierDesc": "Each subsequent grid entry size is multiplied by this factor.",
      "signalsPerDay": "Signals per Day",
      "expectedAccuracy": "Expected Accuracy",
      "tradeFrequency": "Trade Frequency",
      "riskLevel": "Risk Level",
      "profitPotential": "Profit Potential",
      "bestFor": "Best For",
      "activeTraders": "Active traders who want more opportunities",
      "conservativeTraders": "Conservative traders who prefer quality over quantity",
      "high": "High",
      "low": "Low",
      "moderate": "Moderate",
      "higher": "Higher",
      "viewComparison": "View Comparison",
      "hideComparison": "Hide Comparison",
      "testForecastStrategy": "Test Forecast Strategy",
      "testingStrategy": "Testing Strategy...",
      "testStrategyDesc": "Click to test if the forecast strategy would open a trade right now",
      "paperSettingsError": "Paper Settings Error",
      "paperSettingsErrorDesc": "Failed to load paper trading risk settings.",
      "settingsLoadError": "Settings Load Error",
      "settingsLoadErrorDesc": "Failed to load risk settings. Please refresh the page.",
      "configurationRequired": "Configuration Required",
      "configurationRequiredDesc": "Please set investment percentage above 0% before testing strategy.",
      "noTradeSignal": "No Trade Signal",
      "noTradeSignalDesc": "Forecast shows insufficient movement. Upward: {{upward}}%, Downward: {{downward}}%. Need >0.5% to trigger trade.",
      "strategyTestSuccessful": "Strategy Test Successful",
      "strategyTestSuccessfulDesc": "{{direction}} signal detected with {{movement}}% potential movement. {{result}}",
      "paperTradeOpened": "Paper trade opened successfully!",
      "tradeSimulationCompleted": "Trade simulation completed.",
      "strategyTestFailed": "Strategy Test Failed",
      "strategyTestFailedDesc": "Unknown error occurred during strategy test.",
      "strategyTestError": "Strategy Test Error",
      "strategyTestErrorDesc": "Failed to test forecast strategy.",
    },
    "activePositions": {
      "title": "Active Positions",
      "tabs": {
        "all": "all",
        "app": "app",
        "external": "external",
      },
      "close": "Close",
      "closing": "Closing...",
      "refresh": "Refresh",
      "errorLoading": "Error loading active positions",
      "retry": "Retry",
      "noPositions": "No active positions.",
      "noPositionsDesc": "Your active trading positions will appear here once you start trading or enable auto-trading.",
      "noAppPositions": "No active app positions.",
      "noExternalPositions": "No active external positions.",
      "addCredentialsWarning": " Add API credentials to see external positions",
      "externalError": "❌ External positions error: {{error},",
      "externalErrorGeneric": "❌ {{error},",
      "diagnostics": "App: {{app}} | External: {{external},",
      "fields": {
        "size": "Size",
        "entryPrice": "Entry Price",
        "currentPrice": "Current Price",
        "margin": "Margin",
        "liquidationPrice": "Liq. Price",
        "marginRate": "Margin Rate",
        "takeProfit": "TP",
        "stopLoss": "SL",
        "roi": "ROE",
        "none": "None",
        "na": "N/A",
      },
      "positionTypes": {
        "appPositions": "App Positions",
        "externalPositions": "External Positions",
      },
      "exposureWarningTitle": "Exchange exposure detected",
      "exposureWarningMixedDesc": "DeepTrade found external exchange positions alongside app-managed trades. Review the All tab totals against your exchange before enabling auto-trading.",
      "exposureWarningExternalOnlyDesc": "DeepTrade found external exchange positions that are not app-managed. Review your exchange exposure before enabling auto-trading.",
      "headers": {
        "symbol": "Symbol",
        "size": "Size",
        "entryPrice": "Entry Price",
        "markPrice": "Mark Price",
        "liqPrice": "Liq. Price",
        "margin": "Margin",
        "pnlRoi": "PNL (ROI %)",
        "tpSl": "TP/SL",
        "tpSlProjection": "TP/SL Projection",
        "actions": "Actions",
      },
      "errorSettingTakeProfitDesc": "Failed to set Take Profit",
      "stopLossSet": "Stop Loss Set",
      "stopLossSetDesc": "Stop loss set at ${{price}}",
      "errorSettingStopLoss": "Error Setting Stop Loss",
      "errorSettingStopLossDesc": "Failed to set Stop Loss",
      "position": "position",
      "positions": "positions",
      "allPositions": "All Positions",
      "noApiCredentials": "No API Credentials connected",
      "closePosition": "Close Position",
      "closePositionConfirmDesc": "Are you sure you want to close this position? This action cannot be undone.",
      "closePositionConfirmButton": "Close Position"
    },
    "debug": {
      "testSuccessful": "Debug Trade Test Successful",
      "paperTradeOpened": "Paper trade opened successfully!",
      "tradeSimulationCompleted": "Trade simulation completed.",
      "testFailed": "Debug Trade Test Failed",
      "unknownError": "Unknown error occurred during debug trade test.",
      "testError": "Debug Trade Error",
      "testErrorDesc": "Failed to test trade opening.",
    },
    "profitShare": {
      "title": "Profit Share Tracking",
      "loading": "Loading profit share data...",
      "noData": "No profit share data available. Start trading to track your profits.",
      "balanceOverview": "Balance Overview",
      "initialBalance": "Initial Balance:",
      "currentBalance": "Current Balance:",
      "netDeposits": "Net Deposits:",
      "profitAnalysis": "Profit Analysis",
      "tradingProfit": "Trading Profit:",
      "trueProfit": "True Profit:",
      "profitPercentage": "Profit %:",
      "profitShareInfo": "Profit Share",
      "tierRate": "Tier {{tier}} Rate:",
      "amountOwed": "Amount Owed:",
      "status": "Status:",
      "profitable": "Account is in profit - Profit share applies to gains above initial balance",
      "notProfitable": "Account is not yet profitable - No profit share until balance exceeds initial + deposits",
      "profitableDescription": "Your balance is above your initial capital and deposits. Profit share fees will be calculated on the gains shown above.",
      "notProfitableDescription": "Keep trading to move above your initial capital plus deposits. Until then, no profit share is due.",
      "paymentRequired": "Payment Required",
      "paymentDescription": "You have ${{amount}} in profit share fees to pay.",
      "payNow": "Pay Now",
      "viewDetails": "View Profit Share Details",
    },
    "tradingHistory": {
      "title": "Trading History (App Trades Only)",
      "description": "Showing trades initiated by DeepTrade platform",
      "loading": "Loading...",
      "errorTitle": "Unable to load trading history",
      "errorMessage": "We couldn't retrieve your live trading history. Please try again or refresh after confirming your exchange credentials.",
      "headers": {
        "date": "Date",
        "symbol": "Symbol",
        "side": "Side",
        "size": "Size",
        "entry": "Entry",
        "exit": "Exit",
        "fees": "Fees",
        "pnl": "Realized P&L",
        "status": "Status",
      },
      "noHistory": "No trading history found. Start trading to see your history here.",
      "pagination": "Page {{page}} of {{pages}} ({{total}} total trades)",
      "previous": "Previous",
      "next": "Next",
    },
    "blockedUser": {
      "title": "Trading Blocked:",
      "message": "Unpaid profit share for your tier ({{tier}}). Owed: ${{owed}}. Please settle your balance to continue.",
    },
    "tooltips": {
      "balance": "Your current account balance from your connected exchange.",
      "pnlToday": "Realized profit and loss from trades closed today.",
      "monthlyRate": "Your monthly return rate as a percentage.",
      "currentMonthRate": "Realized profit for the current calendar month (month-to-date), as a percentage of your balance at the start of the month.",
      "monthlyAvgRate": "Average monthly profit across complete calendar months since your first trade.",
      "symbolSelector": "Switches the Price Forecast chart and the header price ticker between the trading pairs offered.",
      "winRate": "Percentage of your trades that were profitable."
    }
  },
  "performanceAnalytics": {
    "title": "Performance Analytics",
    "paperMode": "Paper",
    "liveMode": "Live",
    "refresh": "Refresh performance data",
    "noTrades": "No closed trades yet - performance analytics will appear after your first trade closes.",
    "viewCumulative": "Cumulative",
    "viewMonthly": "Monthly",
    "viewStatistics": "Statistics",
    "groupReturns": "Returns",
    "groupRisk": "Risk",
    "groupTrades": "Trades",
    "expectancyUsd": "Expectancy",
    "expectancyR": "Expectancy (R)",
    "profitFactor": "Profit Factor",
    "maxDrawdownPct": "Max DD %",
    "drawdownDuration": "DD Duration",
    "recoveryFactor": "Recovery Factor",
    "sharpe": "Sharpe",
    "sortino": "Sortino",
    "avgWin": "Avg Win",
    "avgLoss": "Avg Loss",
    "maxConsecWins": "Max Consec. Wins",
    "maxConsecLosses": "Max Consec. Losses",
    "bestTrade": "Best Trade",
    "worstTrade": "Worst Trade",
    "avgDuration": "Avg Duration",
    "feesPaid": "Fees Paid",
    "exitReasonsTitle": "Exit Reasons",
    "rDistribution": "R-Multiple Distribution",
    "daysUnit": "d",
    "hoursUnit": "h",
    "range7d": "7D",
    "range30d": "30D",
    "rangeYtd": "YTD",
    "range1y": "1Y",
    "rangeAll": "All",
    "sideBySide": "Drawdown below profit",
    "cumulativeProfit": "Cumulative Profit",
    "drawdown": "Drawdown",
    "monthlyPnl": "Monthly P&L",
    "monthlyAvg": "Monthly Avg",
    "profitUsd": "Profit (USD)",
    "drawdownUsd": "Drawdown (USD)",
    "totalPnl": "Total P&L",
    "currentMonth": "This Month",
    "maxDrawdown": "Max Drawdown",
    "winRate": "Win Rate",
    "totalTrades": "Trades",
    "bySymbol": "Per Symbol",
    "symbol": "Symbol",
    "pnl": "P&L",
    "trades": "Trades"
  },
  "trading": {
    "title": "Trading",
    "signals": "Signals",
    "positions": "Positions",
    "history": "History",
    "long": "Long",
    "short": "Short",
    "autoTrading": "Auto Trading",
    "autoTradingActive": "Auto Trading Active",
    "autoTradingDisabled": "Auto Trading Disabled",
    "autoTradingActiveDesc": "Signals will be executed automatically",
    "autoTradingDisabledDesc": "Enable to automatically execute trading signals",
    "noActiveSignals": "No Active Signals",
    "waitingForSignals": "Waiting for new trading opportunities...",
    "enableAutoTrading": "Enable auto trading to receive signals",
    "refreshSignals": "Refresh Signals",
    "noOpenPositions": "No Open Positions",
    "positionsDesc": "Your active trading positions will appear here",
    "noTradingHistory": "No Trading History",
    "historyDesc": "Your completed trades will appear here",
    "executeTradeButton": "Execute Trade",
    "viewDetailsButton": "View Details",
    "entry": "Entry",
    "stopLoss": "Stop Loss",
    "takeProfit": "Take Profit",
    "confidence": "{{value}}% confidence",
    "strategy": "Strategy",
    "firstTp": "First TP",
    "secondTp": "Second TP",
    "autoMoveStopLoss": "Auto-move SL to breakeven after first TP",
    "tradingMode": "Trading Mode",
    "paperMode": "Paper",
    "liveMode": "Live",
    "paperTradingActive": "Paper Trading Active",
    "liveTradingActive": "Live Trading Active",
    "paperTradingDesc": "Practice trading with virtual funds",
    "liveTradingDesc": "Trade with real money",
    "virtualBalance": "Virtual Balance",
    "unrealizedPnL": "Unrealized P&L",
    "equity": "Equity",
    "paperTradingHelp": "Paper Trading Help",
    "resetAccount": "Reset Account",
    "paperTradingGuide": "Paper Trading Guide",
    "paperModeTitle": "Paper Mode",
    "noProfitSharePaper": "No profit share in paper mode",
    "paperTradingPositions": "Total Positions:",
    "paperTradingHistory": "Paper Trading History",
    "simulatedTradingHistory": "Simulated trading history",
    "noPaperPositions": "No paper trading positions",
    "enableAutoTradingPaper": "Paper trading will automatically generate simulated positions based on market signals",
    "paperTradingMode": "Paper Trading Mode",
    "autoTradingNotAvailable": "Auto-Trading Not Available",
    "autoTradingDisabledPaper": "Auto-trading controls live trading. Paper trading generates signals automatically.",
    "riskManagement": "Risk Management",
    "riskSettings": "Risk Settings",
    "investmentPercentage": "Investment Percentage",
    "leverage": "Leverage",
    "maxLeverage": "Max Leverage",
    "currentTier": "Current Tier",
    "exchange": "Exchange",
    "accountType": "Account Type",
    "marketType": "Market Type",
    "saveSettings": "Save Settings",
    "resetToSafe": "Reset to Safe",
    "saving": "Saving...",
    "riskSettingsSaved": "Risk settings saved successfully",
    "failedToSaveRiskSettings": "Failed to save risk settings",
    "position": "position",
    "internal": "Internal",
    "external": "External",
    "marketClose": "MKT Close",
    "reverse": "Reverse",
    "editTpSl": "TP/SL",
    "refreshActivePositions": "Refresh Active Positions",
    "youHaveNoPosition": "You have no position.",
    "noActiveAppPositions": "No active app positions found.",
    "noActiveExternalPositions": "No active external positions found.",
    "yourActivePositionsWillAppear": "Your active trading positions will appear here.",
    "previous": "Previous",
    "next": "Next",
    "learnMore": "Learn More",
    "paperTradingLabel": "Paper Trading",
    "recentPaperTrades": "Recent Paper Trades",
    "noPaperTrades": "No paper trades yet",
    "paperTradingAutoGenerate": "Paper trading will automatically generate simulated trades based on market signals",
    "activePositionsTitle": "Active Positions",
    "youHaveNoPositions": "You have no position.",
    "yourActivePositionsWillAppearHere": "Your active trading positions will appear here once you start trading or enable auto-trading.",
    "max": "Max",
    "tooltips": {
      "virtualBalance": "Your realized balance from closed trades. Calculated as: Initial Balance + Total Net P&L from all closed trades.",
      "unrealizedPnL": "Profit or loss from your currently open positions. This value changes in real-time based on current market prices and is not yet realized.",
      "equity": "Your total account value including open positions. Calculated as: Virtual Balance + Unrealized P&L. This represents your current net worth in paper trading.",
      "profitAndLoss": "Realized profit and loss from closed trades only. This does not include unrealized P&L from open positions.",
      "balance": "Your current account balance available for trading.",
      "pnlToday": "Realized profit and loss from trades closed today.",
      "monthlyRate": "Your monthly return rate calculated as a percentage of your initial balance.",
      "winRate": "Percentage of closed trades that were profitable. Calculated from completed trades only.",
      "totalReturn": "Your total return percentage based on Virtual Balance. Calculated as: (Current Virtual Balance - Initial Balance) / Initial Balance × 100%."
    }
  },
  "common": {
    "loading": "Loading...",
    "error": "Error",
    "success": "Success",
    "warning": "Warning",
    "info": "Information",
    "confirm": "Confirm",
    "cancel": "Cancel",
    "save": "Save",
    "edit": "Edit",
    "delete": "Delete",
    "close": "Close",
    "back": "Back",
    "next": "Next",
    "previous": "Previous",
    "submit": "Submit",
    "reset": "Reset",
    "search": "Search",
    "filter": "Filter",
    "sort": "Sort",
    "refresh": "Refresh",
    "enable": "Enable",
    "disable": "Disable",
    "active": "Active",
    "inactive": "Inactive",
    "notConnected": "Not connected",
    "tryAgainLater": "Please try again later",
    "toggleOn": "ON",
    "toggleOff": "OFF",
    "yes": "Yes",
    "no": "No",
    "ok": "OK",
    "apply": "Apply",
    "clear": "Clear",
    "select": "Select",
    "upload": "Upload",
    "download": "Download",
    "copy": "Copy",
    "copied": "Copied!",
    "share": "Share",
    "print": "Print",
    "export": "Export",
    "import": "Import",
    "and": "and",
    "on": "ON",
    "off": "OFF",
    "enabled": "enabled",
    "disabled": "disabled",
    "for": "for",
  },
  "wallet": {
    "connect": "Connect Wallet",
    "disconnect": "Disconnect",
    "connected": "Connected",
    "notConnected": "Not Connected",
    "connecting": "Connecting...",
    "balance": "Balance",
    "address": "Address",
    "solanaBlockchain": "Solana Blockchain Powered",
    "solanaTooltip": "Built on Solana - Fast, secure, and scalable blockchain",
    "wrongNetwork": "Wrong Network",
    "switchNetwork": "Switch Network",
    "transactionPending": "Transaction Pending",
    "transactionConfirmed": "Transaction Confirmed",
    "transactionFailed": "Transaction Failed",
    "connectionError": "Connection Error",
    "connectionFailed": "Failed to connect wallet",
    "connectionRejected": "Wallet connection was rejected",
    "walletNotFound": "No Solana wallet found. Please install a wallet like Phantom.",
    "disconnected": "Wallet Disconnected",
    "disconnectedSuccess": "Your wallet has been disconnected successfully.",
    "disconnectError": "Disconnect Error",
    "disconnectFailed": "Failed to disconnect wallet.",
    "confirmDisconnect": "Confirm Wallet Disconnect",
    "confirmDisconnectMessage": "Are you sure you want to disconnect your wallet?",
  },
  "notifications": {
    "autoTradingEnabled": "Auto-trading has been enabled",
    "autoTradingDisabled": "Auto-trading has been disabled",
    "settingsSaved": "Settings have been saved successfully",
    "passwordChanged": "Password changed successfully",
    "profileUpdated": "Profile updated successfully",
    "walletConnected": "Wallet connected successfully",
    "walletDisconnected": "Wallet disconnected",
    "signalExecuted": "Trading signal executed",
    "tradeCompleted": "Trade completed successfully",
    "paymentSuccessful": "Payment processed successfully",
    "tierUpgraded": "Tier upgraded successfully",
    "autoTrading": {
      "enabledTitle": "Auto-Trading Enabled",
      "enabledDescription": "Auto-trading has been enabled successfully. Balance requirement verified.",
      "disabledTitle": "Auto-Trading Disabled",
      "disabledDescription": "Auto-trading has been disabled successfully.",
      "insufficientBalanceTitle": "Insufficient Balance",
      "insufficientBalanceDescription": "Minimum balance of {{required}} USDT required. Your current balance is {{current}} USDT.",
      "missingCredentialsTitle": "Missing API Credentials",
      "missingCredentialsDescription": "Please add and validate your exchange API credentials first.",
      "balanceVerificationFailedTitle": "Balance Verification Failed",
      "balanceVerificationFailedDescription": "Failed to verify account balance. Please check your API credentials.",
      "errorTitle": "Auto-Trading Error",
      "errorDescription": "Failed to toggle auto-trading. Please try again.",
      "systemDisabledTitle": "Auto-Trading Disabled",
      "noApiCredentialsReason": "No exchange API credentials configured. Please add your API keys.",
      "investmentPercentageReason": "Investment percentage is set to 0%. Please adjust your risk settings.",
      "tierMismatchTitle": "Adjust Risk Management for Current Tier",
      "tierMismatchDescription": "Your risk settings exceed your current tier limits. Current leverage: {{leverage}}x. Tier {{currentTier}} maximum: {{max}}x. Please update your Risk Management settings before enabling Auto Trading."
    },
    "paperAutoTrading": {
      "enabledTitle": "Paper Auto-Trading Enabled",
      "enabledDescription": "Paper trading signals will now be executed automatically",
      "disabledTitle": "Paper Auto-Trading Disabled",
      "disabledDescription": "Paper trading signals will no longer be executed automatically",
      "errorTitle": "Paper Auto-Trading Error",
      "errorDescription": "Failed to toggle paper auto-trading"
    }
  },
  "pagination": {
    "show": "Show",
    "perPage": "per page",
    "showing": "Showing {{start}} to {{end}} of {{total}} entries",
    "previous": "Previous",
    "next": "Next"
  },
  "errors": {
    "generic": "Something went wrong. Please try again.",
    "network": "Network error. Please check your connection.",
    "unauthorized": "You are not authorized to perform this action.",
    "forbidden": "Access denied.",
    "notFound": "The requested resource was not found.",
    "serverError": "Server error. Please try again later.",
    "validationError": "Please check your input and try again.",
    "walletNotConnected": "Please connect your wallet first.",
    "insufficientBalance": "Insufficient balance.",
    "transactionFailed": "Transaction failed. Please try again.",
    "apiError": "API error. Please contact support if this persists.",
  },
  "settings": {
    "title": "Settings",
    "pleaseLogIn": "Please Log In",
    "loginRequired": "You need to be logged in to access settings.",
    "goToLogin": "Go to Login",
    "profile": {
      "title": "Profile Information",
      "description": "Update your account profile information.",
      "firstName": "First Name",
      "lastName": "Last Name",
      "email": "Email Address",
      "updateProfile": "Update Profile",
      "updateEmail": "Update Email",
      "sending": "Sending...",
      "profileUpdated": "Profile updated successfully",
      "placeholders": {
        "newEmail": "Enter new email address",
        "currentPassword": "Enter your current password"
      }
    },
    "security": {
      "title": "Security Settings",
      "description": "Manage your account security and authentication.",
      "currentPassword": "Current Password",
      "newPassword": "New Password",
      "confirmPassword": "Confirm New Password",
      "changePassword": "Change Password",
      "changePasswordButton": "Change Password",
      "changing": "Changing...",
      "passwordChanged": "Password changed successfully",
      "enable2FA": "Enable Two-Factor Authentication",
      "disable2FA": "Disable Two-Factor Authentication",
      "disable2FAButton": "Disable 2FA",
      "disabling": "Disabling...",
      "disable2FADescription": "Are you sure you want to disable two-factor authentication? This will make your account less secure.",
      "sendVerificationCode": "Send Verification Code",
      "verifyEmail": "Verify Email",
      "verifyEmailDescription": "We've sent a 6-digit verification code to your email address. Please enter it below to enable 2FA.",
      "verifyEmailToDisable2FA": "Verify Email to Disable 2FA",
      "verifyEmailToDisable2FADescription": "We've sent a 6-digit verification code to your email address. Please enter it below to disable 2FA.",
      "verificationCode": "Verification Code",
      "verifyCode": "Verify Code",
      "verifying": "Verifying...",
      "resendCode": "Resend Code",
      "cancel": "Cancel",
      "twoFactorEnabled": "Two-factor authentication is enabled",
      "twoFactorDisabled": "Two-factor authentication is disabled",
      "placeholders": {
        "currentPassword": "Enter your current password",
        "newPassword": "Enter your new password",
        "confirmPassword": "Confirm your new password",
        "verificationCode": "Enter 6-digit code"
      }
    },
    "language": {
      "title": "Language & Preferences",
      "description": "Customize your language and display preferences.",
      "selectLanguage": "Language",
      "languageDescription": "Choose your preferred language for the interface.",
    },
    "account": {
      "title": "Account Management",
      "description": "Manage your account settings and data.",
      "deleteAccount": "Delete Account",
      "deleteAccountButton": "Delete Account",
      "deleting": "Deleting...",
      "deleteAccountWarning": "This action cannot be undone. This will permanently delete your account and remove your data from our servers.",
      "confirmDelete": "Type 'DELETE' to confirm",
      "accountDeleted": "Account deleted successfully",
      "deleteAccountModal": {
        "title": "Delete Account",
        "description": "Are you sure you want to permanently delete your account?",
        "confirmText": "Delete Account",
        "typedConfirmationText": "DELETE",
        "permanentlyDelete": "This will permanently delete:",
        "consequences": [
          "All your trading data and history",
          "Your account profile and settings",
          "Any active subscriptions or payments",
          "Access to all DeepTrade services"
        ]
      },
      "deactivated": {
        "title": "Account Deactivated",
        "message": "Your account has been deactivated and access to trading features is restricted.",
        "nextSteps": "What to do next:",
        "step1": "Contact our support team for assistance",
        "step2": "Provide your account details and reason for deactivation",
        "step3": "Wait for account review and reactivation",
        "emailSupport": "Email Support",
        "note": "Note:",
        "noteText": "Your account data is preserved and will be restored upon reactivation. Trading activities are temporarily suspended for security reasons."
      }
    },
    "errors": {
      "fillAllFields": "Please fill in all required fields",
      "updateEmailFailed": "Failed to update email address",
      "changePasswordFailed": "Failed to change password",
      "passwordsNotMatch": "Passwords do not match",
      "enable2FAFailed": "Failed to enable 2FA",
      "verify2FAFailed": "Failed to verify 2FA code",
      "disable2FAFailed": "Failed to disable 2FA",
      "disable2FAVerifyFailed": "Failed to verify 2FA disable code",
      "deleteAccountFailed": "Failed to delete account",
      "invalidVerificationCode": "Please enter a valid verification code",
      "invalid6DigitCode": "Please enter a valid 6-digit code"
    },
    "success": {
      "verificationEmailSent": "Verification Email Sent",
      "verificationEmailDescription": "A verification email has been sent to {{email}}. Please check your inbox.",
      "passwordChanged": "Password Changed",
      "passwordChangedDescription": "Your password has been changed successfully.",
      "2FACodeSent": "2FA Code Sent",
      "2FACodeSentDescription": "A verification code has been sent to your email address.",
      "2FAEnabled": "2FA Enabled",
      "2FAEnabledDescription": "Two-factor authentication has been enabled for your account.",
      "2FADisabled": "2FA Disabled",
      "2FADisabledDescription": "Two-factor authentication has been disabled for your account.",
      "verificationCodeSent": "Verification Code Sent",
      "verificationCodeSentDescription": "A verification code has been sent to your email address.",
      "accountDeleted": "Account Deleted",
      "accountDeletedDescription": "Your account has been permanently deleted."
    }
  },
  "accessSecurity": {
    "title": "Access & Security",
    "subtitle": "Monitor your account security and login activity",
    "totalLogins": "Total Logins",
    "successRate": "Success Rate",
    "uniqueIPs": "Unique IPs",
    "twoFactorStatus": "2FA Status",
    "enabled": "Enabled",
    "disabled": "Disabled",
    "currentSession": "Current Session",
    "currentSessionDescription": "Information about your current login session",
    "lastLoginActivity": "Last Login Activity",
    "lastLoginDescription": "Information about your most recent login session",
    "recentLoginActivity": "Recent Login Activity",
    "recentLoginDescription": "Your recent login attempts and sessions",
    "refresh": "Refresh",
    "ipAddress": "IP Address",
    "timestamp": "Timestamp",
    "status": "Status",
    "userAgent": "User Agent",
    "successful": "Successful",
    "failed": "Failed",
    "noLoginActivity": "No login activity found",
    "currentDevice": "Current Device",
    "location": "Location",
    "sessionStart": "Session Start",
    "loginTime": "Login Time",
    "browser": "Browser",
    "never": "Never",
    "unknown": "Unknown",
    "reason": "Reason:",
    "lastThirtyDays": "Last 30 days",
    "loginSuccessRate": "Login success rate",
    "differentLocations": "Different locations",
    "authenticationRequired": "Authentication Required",
    "authenticationRequiredDescription": "Please log in to view your security information",
    "errors": {
      "authenticationError": "Authentication Error",
      "authenticationErrorDescription": "Please log in to access security information",
      "networkError": "Network Error",
      "networkErrorDescription": "Failed to load security data. Please try again.",
      "refreshFailed": "Refresh Failed",
      "refreshFailedDescription": "Failed to refresh security data. Please try again."
    },
    "success": {
      "dataRefreshed": "Data Refreshed",
      "dataRefreshedDescription": "Security data has been updated successfully"
    }
  },
  "tiers": {
    "title": "Tier Management",
    "currentTier": "Current Tier",
    "choosePlan": "Choose Your Plan",
    "tier1": {
      "name": "Tier 1",
      "subtitle": "Free",
      "description": "Perfect for getting started with basic trading signals",
      "features": [
        "Basic trading signals",
        "Manual trade execution",
        "Community support",
        "Basic market analysis"
      ]
    },
    "tier2": {
      "name": "Tier 2",
      "subtitle": "30 days access",
      "description": "Advanced features for serious traders",
      "features": [
        "Advanced AI trading signals",
        "Auto-trading capabilities",
        "Priority support",
        "Advanced analytics",
        "Risk management tools"
      ]
    },
    "tier3": {
      "name": "Tier 3",
      "subtitle": "NFT Required",
      "description": "Premium tier with exclusive features",
      "features": [
        "Premium trading signals",
        "Advanced auto-trading",
        "VIP support",
        "Exclusive market insights",
        "Custom strategies"
      ]
    },
    "activate": "Activate",
    "current": "Current",
    "paymentRequired": "Payment Required",
    "nftRequired": "NFT Required",
    "membershipStatus": "Membership Status",
    "active": "Active",
    "expired": "Expired",
    "daysRemaining": "Days remaining",
    "expiresOn": "Expires on",
  },
  "apiCredentials": {
    "title": "API Credentials",
    "description": "Manage your exchange API credentials for trading",
    "addCredentials": "Add API Credentials",
    "exchange": "Exchange",
    "exchanger": "Exchanger",
    "apiKey": "API Key",
    "apiSecret": "API Secret",
    "secretKey": "Secret Key",
    "passphrase": "Passphrase",
    "testConnection": "Test Connection",
    "save": "Save Credentials",
    "edit": "Edit",
    "delete": "Delete",
    "status": "Status",
    "connected": "Connected",
    "disconnected": "Disconnected",
    "invalid": "Invalid",
    "active": "Active",
    "testing": "Testing...",
    "connectionSuccess": "Connection successful",
    "connectionFailed": "Connection failed",
    "credentialsAdded": "API credentials added successfully",
    "credentialsUpdated": "API credentials updated successfully",
    "credentialsDeleted": "API credentials deleted successfully",
    "credentialAddedLimitReached": "Credential added (limit reached)",
    "requiredMarketType": "Required Market Type",
    "connectedTo": "Connected to",
    "connectionNeedsAttention": "Connection to",
    "needsAttention": "needs attention",
    "lastUpdated": "Last updated",
    "updateCredentialsMessage": "Please update your API credentials to continue trading",
    "disconnect": "Disconnect",
    "disconnecting": "Disconnecting...",
    "setAsActive": "Set as Active",
    "activating": "Activating...",
    "autoTrading": "Auto Trading",
    "enableAutoTradingDesc": "Enable automated trading for this exchange",
    "tier2LimitReached": "Tier 2 limit reached: up to two CEX credentials allowed.",
    "add": "Add",
    "update": "Update",
    "connectionValidated": "Connection validated!",
    "autoTradingStatusChanged": "Auto trading has been",
    "marketTypes": {
      "futures": "Futures",
      "spot": "Spot",
      "binanceNote": "Futures trading - leverage up to 125x available",
      "binanceUsNote": "Spot trading with margin - leverage up to 3x available",
      "krakenNote": "Futures trading - leverage up to 100x available",
      "bybitNote": "Futures trading - leverage up to 100x available",
      "hyperliquidNote": "Perpetual futures trading - leverage up to 50x available",
      "standardNote": "Standard spot trading",
    },
    "errors": {
      "loadFailed": "Failed to load credentials",
      "keysRequired": "API Key and Secret Key are required",
      "tierUndetermined": "Unable to determine user tier",
      "tier1Limit": "Tier 1 allows only one API credential",
      "tier2PerCex": "Tier 2 allows only one credential per CEX",
      "tier2Limit": "Tier 2 allows up to two credentials (one per CEX)",
      "tier3Limit": "Tier 3 allows only one credential for all CEXs",
      "saveFailed": "Save failed",
      "validationFailed": "Validation failed",
      "activationFailed": "Activation failed",
      "deactivationFailed": "Deactivation failed",
      "noCredentials": "No credentials found for this exchange",
      "autoTradingUpdateFailed": "Auto Trading Update Failed",
      "autoTradingUpdateFailedDesc": "Failed to update auto trading settings",
    },
    "success": {
      "credentialsSaved": "Credentials saved",
      "connectionSuccessful": "Connection successful",
      "activated": "Activated",
      "deactivated": "Deactivated",
      "autoTradingEnabled": "Auto Trading Enabled",
      "autoTradingDisabled": "Auto Trading Disabled",
    }
  },
  "referrals": {
    "title": "Referral Dashboard",
    "loading": "Loading referral dashboard...",
    "referralLink": "Referral Link",
    "referralLinkDescription": "Share this link to earn {{rate}}% from your referrals' profit share payments.",
    "settings": {
      "title": "Settings",
      "referralSettings": "Referral Settings",
      "solanaWalletAddress": "Solana Wallet Address",
      "walletPlaceholder": "Enter your Solana wallet address for payouts",
      "minimumPayoutAmount": "Minimum Payout Amount ($)",
      "enableAutomaticPayments": "Enable automatic payments",
      "autoPaymentEnabled": "When enabled, you'll automatically receive referral payouts when they are due.",
      "autoPaymentDisabled": "When disabled, you will not receive any referral payouts until this is enabled again.",
      "saveSettings": "Save Settings",
      "importantWarning": "Important Warning:",
      "warnings": {
        "ensureCorrectAddress": "Please make sure your Solana wallet address is correct",
        "doubleCheckAddress": "Double-check the address before saving",
        "notResponsible": "DeepTrade is NOT responsible for payments sent to incorrect or invalid addresses",
        "cannotRecover": "Funds lost due to incorrect addresses cannot be recovered"
      },
      "minimumPayoutRequirements": "Minimum Payout Requirements:",
      "requirements": {
        "minimumAmount": "Minimum payout amount: $10.00 USDT",
        "gasFeeDeducted": "Solana network gas fees will be deducted from the payout",
        "estimatedGasFee": "Estimated gas fee: ~0.001 SOL (~$0.10-$0.50)",
        "payoutsProcessed": "Payouts are processed in USDT on the Solana network"
      }
    },
    "tierNames": {
      "tier1": "Tier 1",
      "tier2": "Tier 2",
      "tier3": "Tier 3"
    },
    "overview": {
      "totalReferrals": "Total Referrals",
      "activeCount": "{{count}} active",
      "totalEarnings": "Total Earnings",
      "totalPaid": "${{amount}} paid",
      "pendingPayout": "Pending Payout",
      "requestPayout": "Request Payout",
      "earningRate": "Earning Rate"
    },
    "actions": {
      "copy": "Copy",
      "generateNew": "Generate New"
    },
    "recentActivity": {
      "recentReferrals": "Recent Referrals",
      "recentEarnings": "Recent Earnings",
      "noReferrals": "No referrals yet. Start sharing your link!",
      "noEarnings": "No earnings yet. Referrals need to start trading!",
      "earned": "${{amount}} earned"
    },
    "status": {
      "verified": "Verified",
      "pending": "Pending",
      "paid": "Paid"
    },
    "messages": {
      "copied": "Copied!",
      "linkCopied": "Referral link copied to clipboard",
      "newCodeGenerated": "New referral code generated",
      "profileUpdated": "Profile updated successfully",
      "payoutRequestSubmitted": "Payout request submitted"
    },
    "errors": {
      "failedToLoadData": "Failed to load referral data",
      "failedToGenerateCode": "Failed to generate new code",
      "invalidMinimumPayout": "Invalid Minimum Payout",
      "minimumPayoutRequired": "Minimum payout must be at least $10.00",
      "invalidWalletAddress": "Invalid Wallet Address",
      "validWalletRequired": "Please enter a valid Solana wallet address",
      "failedToUpdateProfile": "Failed to update profile",
      "failedToRequestPayout": "Failed to request payout"
    }
  },
  "help": {
    "title": "Help & Support",
    "description": "Find answers, review our legal policies, or get in touch with the DeepTrade support team.",
    "faqTab": "Help FAQ",
    "legalTab": "Help Legal",
    "contactTab": "Help Contact",
    "searchFAQ": "Search FAQ...",
    "faqSection": {
      "categories": {
        "general": "General",
        "trading": "Trading",
        "tiers": "Tiers",
        "setup": "Setup",
        "security": "Security",
        "billing": "Billing"
      },
      "items": {
        "whatIsDeepTrade": {
          "question": "What is DeepTrade?",
          "answer": "DeepTrade is our AI-powered trading platform that provides automated trading signals and portfolio automation tools for supported exchanges."
        },
        "howSignalsWork": {
          "question": "How do the AI signals work?",
          "answer": "Our machine learning models analyze market data around the clock and generate trading signals that balance risk, reward, and probability."
        },
        "tierLevels": {
          "question": "What are the membership tiers?",
          "answer": "DeepTrade offers multiple tiers that unlock higher leverage, advanced tooling, and lower profit-share fees as you upgrade."
        },
        "connectExchange": {
          "question": "How do I connect an exchange?",
          "answer": "Visit the API Credentials page, add your exchange keys, and follow the prompts to link your account securely."
        },
        "dataSecure": {
          "question": "Is my data secure?",
          "answer": "Yes. We encrypt API keys, restrict internal access, and continuously monitor our infrastructure for threats."
        },
        "profitShares": {
          "question": "How do profit-share payments work?",
          "answer": "Profit-share fees are only charged on realized trading gains produced by DeepTrade signals."
        },
        "cancelSubscription": {
          "question": "Can I cancel my subscription?",
          "answer": "You can cancel anytime from the tier management page. Your access remains active until the end of the billing cycle."
        },
        "supportedExchanges": {
          "question": "Which exchanges are supported?",
          "answer": "We support major exchanges such as Binance, Bybit, Kraken, and others listed on the API Credentials page."
        }
      }
    },
    "legal": {
      "documents": "Legal Documents",
      "englishNotice": "Currently available in English only.",
      "privacy": {
        "title": "Privacy Policy",
        "description": "Understand what data we collect, how we use it, and the safeguards we put in place to protect your information.",
        "viewFull": "View full privacy policy",
        "highlights": {
          "title": "Privacy highlights",
          "item1": "Account information and preferences are securely stored.",
          "item2": "Trading data is used to improve signals and user experience.",
          "item3": "We never sell or rent personal information to third parties.",
          "item4": "Security best practices protect your API keys and payment data."
        }
      },
      "terms": {
        "title": "Terms of Service",
        "description": "Review the rules, responsibilities, and legal terms that apply when you use DeepTrade.",
        "viewFull": "View full terms of service",
        "highlights": {
          "title": "Key terms",
          "item1": "You remain responsible for trades executed using DeepTrade.",
          "item2": "Cryptocurrency markets involve significant risk.",
          "item3": "Subscriptions renew automatically until cancelled.",
          "item4": "Violations may result in account suspension."
        }
      },
      "risks": {
        "title": "Trading Risk Disclosure"
      }
    },
    "contact": {
      "title": "Contact Support",
      "form": {
        "name": "Full Name",
        "email": "Email Address",
        "subject": "Subject",
        "message": "Message",
        "placeholders": {
          "name": "Enter your name",
          "email": "Enter your email",
          "subject": "Tell us how we can help",
          "message": "Provide details so we can assist you quickly"
        },
        "sendMessage": "Send Message",
        "sending": "Sending..."
      },
      "success": {
        "messageSent": "Message sent successfully",
        "thankYou": "Thank you! Our support team will reply as soon as possible."
      },
      "errors": {
        "missingInformation": "Missing information",
        "fillAllFields": "Please complete every field before submitting.",
        "failedToSend": "Unable to send message",
        "error": "Something went wrong",
        "tryAgainLater": "We couldn't send your request. Please try again later."
      },
      "guidelines": {
        "title": "Support Guidelines",
        "before": {
          "title": "Before contacting us",
          "item1": "Review the FAQ for quick answers.",
          "item2": "Check the status page for ongoing incidents.",
          "item3": "Confirm your API credentials are up to date.",
          "item4": "Gather screenshots or logs that show the issue."
        },
        "when": {
          "title": "When to reach out",
          "item1": "Billing or subscription questions",
          "item2": "Trading automation or API issues",
          "item3": "Security concerns or suspicious activity",
          "item4": "Feature requests or general feedback"
        },
        "notice": {
          "title": "Important notice",
          "message": "Support responses are prioritized for high-severity issues. Include as much detail as possible to speed up resolution."
        }
      }
    }
  },
  "paperTradingHelp": {
    "title": "Paper Trading Guide",
    "whatIsPaperTrading": "What is Paper Trading?",
    "whatIsPaperTradingDesc": "Paper trading is a simulated trading environment that allows you to practice trading strategies using virtual money instead of real funds. It's the perfect way to test our AI trading system without any financial risk.",
    "keyBenefits": "Key Benefits",
    "riskFree": "Risk-Free Learning",
    "riskFreeDesc": "Practice with virtual funds - no real money at risk",
    "realMarketData": "Real Market Data",
    "realMarketDataDesc": "Experience live market conditions and price movements",
    "aiTradingSystem": "AI Trading System",
    "aiTradingSystemDesc": "Test our advanced AI signals in a safe environment",
    "buildConfidence": "Build Confidence",
    "buildConfidenceDesc": "Develop your trading skills before using real money",
    "howItWorks": "How It Works",
    "step1": "Toggle to Paper Mode",
    "step1Desc": "Switch the trading mode toggle to enable paper trading",
    "step2": "Virtual Balance",
    "step2Desc": "Start with $10,000 virtual balance (can be reset)",
    "step3": "AI Signals",
    "step3Desc": "Our AI generates trading signals based on real market data",
    "step4": "Simulated Trades",
    "step4Desc": "Trades are executed virtually - track your performance",
    "importantNotes": "Important Notes",
    "noRealMoney": "No Real Money",
    "noRealMoneyDesc": "Paper trading uses virtual funds only. No real profits or losses occur.",
    "noRealFees": "No Real Fees",
    "noRealFeesDesc": "Trading fees and slippage are simulated for realistic experience.",
    "performanceTracking": "Performance Tracking",
    "performanceTrackingDesc": "All statistics and analytics are based on simulated trades only.",
    "readyToStart": "Ready to Get Started?",
    "readyToStartDesc": "Toggle the \"Trading Mode\" switch to \"Paper\" and start practicing with virtual funds today!",
    "buildConfidenceFooter": "💪 Build confidence with paper trading before risking real money!",
    "resetModal": {
      "title": "Reset Paper Trading Account",
      "warning": "Warning: This action cannot be undone",
      "warningDesc": "This will permanently delete all your paper trading history and reset your virtual balance.",
      "resetLimitReached": "Daily Reset Limit Reached",
      "resetLimitReachedDesc": "You have used all 3 resets for today. Limit resets at midnight UTC.",
      "resetsRemaining": "Resets remaining today: {{count},",
      "currentBalance": "Current Balance: ${{balance},",
      "resetToDefault": "Reset to default $10,000",
      "customBalance": "Set custom balance",
      "customBalanceDesc": "Enter a custom starting balance (minimum $1,000)",
      "confirmationRequired": "Type 'RESET' to confirm",
      "confirmationPlaceholder": "Type RESET here...",
      "confirmationRequiredError": "Please type 'RESET' to confirm",
      "invalidBalance": "Invalid Balance",
      "invalidBalanceDesc": "Please enter a valid positive balance amount",
      "resetting": "Resetting...",
      "resetButton": "Reset Account",
    },
    "howItWorksModal": {
      "title": "How Paper Trading Works",
      "step1": "Start with $10,000 virtual balance (configurable)",
      "step2": "Enable auto-trading to let AI execute trades automatically",
      "step3": "Monitor performance and adjust risk settings as needed",
      "step4": "Switch to live trading when you're confident in the system",
    },
    "importantNotesDetails": {
      "noRealMoneyTitle": "No Real Money:",
      "noRealMoneyDesc": "Paper trading uses virtual funds only. No real profits or losses occur.",
      "noFeesTitle": "No Fees:",
      "noFeesDesc": "Paper trading doesn't generate profit-sharing fees since no real profits are made.",
      "resetLimitTitle": "Reset Limit:",
      "resetLimitDesc": "You can reset your paper account up to 3 times per day.",
    },
    "benefits": {
      "title": "Benefits of Paper Trading",
      "riskFreeLearning": "Risk-Free Learning",
      "riskFreeLearningDesc": "Practice without losing real money while learning how our AI system works",
      "strategyTesting": "Strategy Testing",
      "strategyTestingDesc": "Test different risk settings and see how they perform over time",
      "realMarketData": "Real Market Data",
      "realMarketDataDesc": "Uses live market prices and real ML forecasts for authentic experience",
    },
    "gettingStarted": {
      "title": "Ready to Get Started?",
      "description": "Toggle the \"Trading Mode\" switch to \"Paper\" and start practicing with virtual funds today!",
      "buildConfidenceFooter": "💪 Build confidence with paper trading before risking real money!",
    },
    "perfectForBeginners": "💡 Perfect for beginners and experienced traders who want to test new strategies!",
    "availableToAllTitle": "Available to All:",
    "availableToAllDesc": "Paper trading is available to all users regardless of tier level.",
    "gotItThanks": "Got it, thanks!",
  },

  "paperTradingDashboard": {
    "performance": {
      "title": "Paper Trading Performance",
      "refresh": "Refresh",
      "reset": "Reset",
      "totalReturn": "Total Return",
    },

    "errors": {
      "loadFailed": "Failed to load paper trading data",
    }
  },
  "tradingSignalStatus": {
    "title": "Trading Signal Status",
    "description": "Live status from the system-wide signal broadcast",
    "signalLabel": "Signal",
    "confidenceLabel": "AI Signal Confidence",
    "lastUpdateLabel": "Last update",
    "errorLoading": "Failed to load trading status",
    "signals": {
      "HOLD": "HOLD",
      "BUY": "BUY",
      "SELL": "SELL",
      "LONG": "Long",
      "SHORT": "Short",
      "long": "Long",
      "short": "Short"
    }
  },
  "tier": {
    "choosePlan": "Choose Your Plan",
    "membershipStatus": {
      "title": "Membership Status",
      "active": "Active",
      "expired": "Expired",
      "daysRemaining": "Days remaining:",
      "expiresOn": "Expires on:"
    },
    "modals": {
      "activateTier": "Activate {{tierName}} Tier",
      "confirmActivation": "Are you sure you want to activate the {{tierName}} tier?",
      "confirmTierChange": "Confirm Tier Change",
      "confirmSwitch": "Are you sure you want to switch to the {{tierName}} tier?",
      "cancel": "Cancel",
      "activate": "Activate",
      "confirm": "Confirm"
    },
    "tiers": {
      "starter": {
        "name": "Tier 1: Starter",
        "price": "Free",
        "description": "Perfect for getting started",
        "features": [
          "Basic Trading Signals",
          "Trading Leverage up to 2.5x",
          "Limited to One API Access",
          "40% Profit Share Fee"
        ]
      },
      "pro": {
        "name": "Tier 2: Pro",
        "description": "For serious traders",
        "accessInstructions": "To access this tier, use the wallet button in the top navigation bar to connect your Solana wallet and pay for 30 days access.",
        "features": [
          "Advanced Trading Signals",
          "Trading Leverage up to 5x",
          "Limited to Two API Access",
          "20% Profit Share Fee"
        ]
      },
      "nftElite": {
        "name": "Tier 3: NFT Elite",
        "price": "Lifetime",
        "description": "For verified Capitolchilax NFT collectors.",
        "visitWebsite": "Visit capitolchilax.com",
        "accessInstructions": "To access this tier, use the wallet button in the top navigation bar to connect and verify your NFT eligibility.",
        "features": [
          "Advanced Trading Signals",
          "Trading Leverage up to 10x",
          "Unlimited API Connections",
          "10% Profit Share Fee",
          "No Monthly Membership Fee"
        ]
      }
    },
    "status": {
      "currentTier": "Current Tier",
      "alreadyUnlocked": "Already Unlocked",
      "yourCurrentTier": "Your Current Tier",
      "currentlyOn": "You are currently on {{tierName}}"
    },
    "buttons": {
      "paymentRequired": "Payment Required",
      "connectWallet": "Connect Wallet",
      "nftRequired": "NFT Required",
      "select": "Select {{tierName}}",
      "activate": "Activate {{tierName}}",
      "payToReactivate": "Pay to Reactivate",
      "payWithToken": "Pay with {{token}}",
      "connectWalletFirst": "Connect Wallet First",
      "proceedWithPayment": "Proceed with Payment"
    },
    "nft": {
      "verifyingOwnership": "Verifying NFT ownership...",
      "nftRequired": "You need to own a specific NFT to access this tier",
      "nftOwned": "You own an NFT from the required collection."
    },
    "payment": {
      "thirtyDaysAccess": "30 Days Access",
      "thirtyDaysAccessDue": "30 Days Access Due",
      "paymentDetails": "Payment Details",
      "amount": "Amount:",
      "network": "Network:",
      "wallet": "Wallet:",
      "connectionStatus": "Connection Status:",
      "connected": "Connected",
      "disconnected": "Disconnected",
      "processing": "Processing Payment",
      "processingDescription": "Please confirm the transaction in your wallet and wait for confirmation...",
      "successful": "Payment Successful!",
      "successDescription": "Your payment has been processed successfully. Your tier will be updated shortly.",
      "failed": "Payment Failed",
      "failedDescription": "There was an error processing your payment. Please try again.",
      "tryAgain": "Try Again",
      "thirtyDaysAccessDescription": "Complete your payment for 30 days access to activate Pro features.",
      "importantNotes": "Important Notes",
      "paymentWarnings": [
        "Ensure you have sufficient {{token}} in your wallet",
        "Keep some SOL for transaction fees (~0.001 SOL)",
        "Payment will be processed on Solana mainnet",
        "Transaction is irreversible once confirmed"
      ]
    },
    "paymentHistory": {
      "title": "Payment History",
      "profitShare": "Profit Share",
      "thirtyDaysAccess": "30 Days Access",
      "billingPeriod": "${{amount}} due for {{startDate}} - {{endDate}}"
    },
    "errors": {
      "paymentRequired": "You must pay your outstanding profit share fees (${{amount}}) before changing tiers.",
      "downgradeNotAllowed": "You cannot downgrade from Tier {{currentTier}} to Tier {{newTier}}. You can only upgrade to higher tiers.",
      "downgradeNotAllowedTitle": "Downgrade Not Allowed",
      "walletConnectionRequired": "Please connect your Solana wallet to access Tier 2.",
      "walletConnectionRequiredTitle": "Wallet Connection Required",
      "nftRequired": "You need to own a specific NFT to access this tier.",
      "membershipExpired": "Your Tier 2 membership has expired. You have been downgraded to Starter tier.",
      "membershipExpiredTitle": "Membership Expired",
      "walletDisconnected": "Your tier status has been preserved.",
      "walletDisconnectedTitle": "Wallet Disconnected",
      "walletDisconnectedWithDays": "Your Tier 2 access remains active for {{days}} more days.",
      "couldNotValidate": "Your tier status has been preserved. Reconnect your wallet to make payments.",
      "failedToFetchTier": "Failed to fetch tier status",
      "errorLoadingTier": "Error loading tier information",
      "dataFetchError": "Failed to fetch tier data: {{error}}",
      "dataFetchErrorTitle": "Data Fetch Error",
      "walletNotConnected": "Please connect your Solana wallet to proceed with payment.",
      "walletNotConnectedTitle": "Wallet Not Connected",
      "paymentFailed": "There was an error processing your payment. Please try again.",
      "paymentSuccessful": "Your {{type}} payment has been processed successfully!",
      "activationFailed": "Failed to activate tier. Please try again."
    },
    "profitShare": {
      "paymentDueTitle": "Profit Share Payment Due",
      "accountDisabled": "Account Disabled",
      "status": "Profit Share Status",
      "paymentWasDue": "Payment was due:",
      "paymentDue": "Payment due:",
      "nextPaymentDeadline": "Next payment deadline:",
      "payNow": "Pay Now",
      "payToReactivate": "Pay to Reactivate",
      "overdue": "OVERDUE",
      "accountDisabledMessage": "Account Disabled - Payment Required",
      "paymentOverdue": "Payment Overdue",
      "paymentDueSoon": "Payment Due Soon",
      "wasDue": "Was due:",
      "due": "Due:",
      "youOwe": "You owe ${{amount}} in profit share fees",
      "allCaughtUp": "No outstanding profit share fees - You're all caught up! 🎉",
      "scheduleInfo": "Profit share payments are due every Saturday at 0:00 GMT",
      "disableWarning": "Accounts are disabled every Saturday at 0:00 GMT for unpaid fees",
      "deadlineApproaching": "Payment Deadline Approaching",
      "deadlineMessage": "Your profit share payment of ${{amount}} is due by Saturday at 0:00 GMT. Your account will be automatically disabled if payment is not received by the deadline.",
      "accountTradingDisabled": "Account Trading Disabled",
      "accountDisabledDescription": "Your trading account is disabled due to outstanding profit share fees. Pay ${{amount}} to reactivate trading.",
      "accountSuspendedWarning": "⚠️ Your account has been disabled due to unpaid fees. Trading is suspended until payment is made.",
      "disabledOn": "Disabled on:",
      "deadline": "Deadline:"
    },
    "coupon": {
      "redeemButton": "Have a Coupon? Redeem Here",
      "redeemDescription": "Upgrade your tier instantly with a coupon code"
    },
    "pricing": {
      "free": "Free",
      "lifetime": "Lifetime",
      "thirtyDayAccess": "${{price}} for 30-day access"
    },
    "success": {
      "tierUpdated": "Your tier has been updated to {{tierName}}.",
      "tierActivated": "Your tier has been updated successfully.",
      "paymentSuccessful": "Your {{type}} payment has been processed successfully!"
    },
    "notifications": {
      "accountDisabled": {
        "title": "🚫 Account Disabled",
        "description": "Your account has been disabled due to unpaid profit share fees of ${{amount}}. Please make a payment to reactivate your account."
      },
      "paymentOverdue": {
        "title": "⚠️ Payment Overdue",
        "description": "Your profit share payment of ${{amount}} is overdue. Your account may be disabled soon."
      },
      "paymentReminder": {
        "title": "⏰ Payment Reminder",
        "description": "Your profit share payment of ${{amount}} is due by Saturday 0:00 GMT. Please pay to avoid account suspension."
      }
    }
  },
  "redeemCoupon": {
    "title": "Redeem Coupon",
    "subtitle": "Enter your coupon code to upgrade your tier",
    "currentTier": "Current Tier:",
    "couponCode": "Coupon Code",
    "placeholder": "Enter coupon code (e.g., DTABC123)",
    "formatHint": "Code must be 4-20 characters, letters and numbers only",
    "redeemButton": "Redeem Coupon",
    "redeeming": "Redeeming...",
    "backToTier": "← Back to Tier Management",
    "tierNames": {
      "starter": "Starter (Free)",
      "pro": "Pro (Premium)",
      "vip": "VIP (Elite)",
      "unknown": "Unknown"
    },
    "howItWorks": {
      "title": "How it works:",
      "steps": [
        "Enter your coupon code above",
        "Your tier will be upgraded automatically",
        "You'll get immediate access to new features",
        "Each coupon can only be used once"
      ]
    },
    "errors": {
      "invalidInput": "Invalid Input",
      "enterCouponCode": "Please enter a coupon code",
      "invalidFormat": "Invalid Format",
      "formatRequirement": "Coupon code must be 4-20 characters, letters and numbers only",
      "redemptionFailed": "Redemption Failed",
      "failedToRedeem": "Failed to redeem coupon",
      "networkError": "Network Error",
      "connectionFailed": "Failed to connect to server. Please try again."
    },
    "success": {
      "redeemed": "Coupon Redeemed Successfully!",
      "validFor": "(Valid for {{days}} days)"
    }
  },
  "oauthCallback": {
    "loading": {
      "title": "Authenticating...",
      "description": "Please wait while we complete your authentication."
    },
    "error": {
      "title": "Authentication Error",
      "returnToLogin": "Return to Login",
      "tryAgain": "Try Again"
    },
    "completing": {
      "title": "Completing Authentication",
      "description": "Please wait while we log you in..."
    },
    "errors": {
      "authenticationFailed": "Authentication failed",
      "noAuthorizationCode": "No authorization code received from the OAuth provider",
      "invalidServerResponse": "Invalid response from server",
      "unexpectedError": "An unexpected error occurred during authentication",
      "noAdditionalDetails": "No additional details available"
    }
  },
  "resetPassword": {
    "validation": {
      "minLength": "Password must be at least 8 characters long",
      "uppercase": "Password must contain at least one uppercase letter",
      "lowercase": "Password must contain at least one lowercase letter",
      "number": "Password must contain at least one number",
      "specialChar": "Password must contain at least one special character"
    },
    "errors": {
      "invalidToken": "Invalid Token",
      "invalidTokenDescription": "This password reset link is invalid or has expired.",
      "validationFailed": "Validation Failed",
      "validationFailedDescription": "Failed to validate reset token. Please try again.",
      "fillAllFields": "Please fill in all fields",
      "passwordsDoNotMatch": "Passwords do not match",
      "requirementsNotMet": "Password Requirements Not Met",
      "resetFailed": "Failed to reset password",
      "resetFailedTitle": "Reset Failed"
    },
    "success": {
      "title": "Password Reset Successful",
      "description": "Your password has been successfully updated.",
      "passwordUpdated": "✓ Password Updated",
      "securityNote": "You can now log in with your new password. For security, you've been logged out of all devices.",
      "continueToLogin": "Continue to Login"
    },
    "validating": {
      "title": "Validating Reset Link",
      "description": "Please wait while we validate your password reset link..."
    },
    "invalid": {
      "title": "Invalid Reset Link",
      "description": "This password reset link is invalid or has expired.",
      "linkInvalid": "Link Invalid",
      "reason1": "• The reset link may have expired (links expire after 1 hour)",
      "reason2": "• The link may have already been used",
      "reason3": "• The link may be malformed or incomplete",
      "requestNewLink": "Request New Reset Link",
      "backToLogin": "Back to Login"
    },
    "form": {
      "title": "Reset Your Password",
      "description": "Enter your new password for {{email}}",
      "newPassword": "New Password",
      "newPasswordPlaceholder": "Enter your new password",
      "confirmPassword": "Confirm New Password",
      "confirmPasswordPlaceholder": "Confirm your new password",
      "updating": "Updating Password...",
      "updateButton": "Update Password",
      "backToLogin": "Back to Login"
    },
    "requirements": {
      "title": "Password Requirements",
      "minLength": "• At least 8 characters long",
      "caseLetters": "• Include uppercase and lowercase letters",
      "number": "• Include at least one number",
      "specialChar": "• Include at least one special character (!@#$%^&*(),.?\":{}|<>)"
    },
    "expiration": {
      "notice": "This reset link expires at {{time}}"
    }
  },
  "twoFAResetRequest": {
    "securityQuestions": {
      "question1": "When was your last successful trade happened?",
      "question2": "What tier are you currently on (1, 2, or 3)?",
      "question3": "What exchange do you primarily use for trading?"
    },
    "errors": {
      "fillRequiredFields": "Please fill in all required fields (email, full name, and reason)",
      "submitFailed": "Failed to submit 2FA reset request",
      "requestFailed": "Request Failed"
    },
    "success": {
      "requestSubmitted": "Request Submitted",
      "title": "Request Submitted",
      "description": "Your 2FA reset request has been submitted for review.",
      "submittedSuccessfully": "✓ Request Submitted Successfully",
      "email": "Email",
      "requestId": "Request ID",
      "status": "Status",
      "pendingReview": "Pending Review",
      "whatHappensNext": "What happens next?",
      "step1": "1. Our security team will review your request and verification information",
      "step2": "2. We may contact you for additional verification if needed",
      "step3": "3. You will receive an email notification once processed",
      "step4": "4. If approved, your 2FA will be disabled and you can set it up again",
      "backToLogin": "Back to Login",
      "submitAnother": "Submit Another Request"
    },
    "form": {
      "title": "2FA Reset Request",
      "description": "Lost access to your 2FA device? Fill out this form to request a reset from our security team.",
      "securityNotice": "⚠️ Important Security Notice",
      "notice1": "• This process requires manual review by our security team",
      "notice2": "• Provide as much accurate information as possible to speed up verification",
      "notice3": "• False information will result in request rejection",
      "notice4": "• Processing time: 1-3 business days",
      "requiredInformation": "Required Information",
      "emailAddress": "Email Address *",
      "fullName": "Full Name *",
      "reason": "Reason for 2FA Reset *",
      "additionalVerification": "Additional Verification (Optional but Recommended)",
      "lastLoginDate": "Approximate Last Login Date",
      "accountCreationDate": "Approximate Account Creation Date",
      "recentActivity": "Recent Account Activity",
      "securityQuestionsTitle": "Security Questions",
      "securityQuestionsDescription": "Answer these questions to help verify your identity. Leave blank if you don't know.",
      "submittingRequest": "Submitting Request...",
      "submitButton": "Submit 2FA Reset Request",
      "backToLogin": "Back to Login",
      "rememberDevice": "Remember your 2FA device?",
      "tryVerification": "Try 2FA verification",
      "placeholders": {
        "email": "Enter your account email address",
        "fullName": "Enter your full name as registered",
        "reason": "Explain why you need to reset your 2FA (e.g., lost phone, broken device, etc.)",
        "lastLoginDate": "e.g., December 15, 2024",
        "accountCreationDate": "e.g., November 2024",
        "recentActivity": "Describe recent activities (trades, tier changes, payments, etc.)",
        "yourAnswer": "Your answer"
      }
    }
  },
  "verify2FA": {
    "title": "Two-Factor Authentication",
    "description": "A 6-digit verification code has been sent to your email address",
    "form": {
      "verificationCode": "Verification Code",
      "placeholder": "Enter 6-digit code",
      "verifying": "Verifying...",
      "verifyCode": "Verify Code"
    },
    "errors": {
      "sessionExpired": "Session Expired",
      "pleaseLoginAgain": "Please log in again.",
      "enterCode": "Please enter the verification code",
      "codeMustBe6Digits": "Verification code must be 6 digits",
      "verificationFailed": "Verification failed",
      "verificationFailedTitle": "Verification Failed",
      "invalidCode": "Invalid verification code",
      "failedToResendCode": "Failed to resend code",
      "failedToResendTitle": "Failed to Resend"
    },
    "success": {
      "loginSuccessful": "Login Successful",
      "welcomeBack": "Welcome back!",
      "codeSent": "Code Sent",
      "codeSentDescription": "A new verification code has been sent to your email."
    },
    "help": {
      "needHelp": "Need help?",
      "sending": "Sending...",
      "resendCode": "Resend Code",
      "backToLogin": "Back to Login",
      "lostDevice": "Lost 2FA Device? Request Reset",
      "didntReceiveCode": "Didn't receive the code? Check your spam folder or",
      "resendIt": "resend it",
      "byContinuing": "By continuing, you agree to our",
      "termsOfService": "Terms of Service",
      "privacyPolicy": "Privacy Policy"
    }
  },
  "forgotPassword": {
    "errors": {
      "emailRequired": "Email address is required",
      "sendFailed": "Failed to send password reset email",
      "requestFailed": "Request Failed"
    },
    "success": {
      "emailSent": "Reset Email Sent",
      "title": "Check Your Email",
      "description": "If an account with this email exists, you will receive a password reset link.",
      "emailSentTitle": "Email Sent",
      "emailSentMessage": "We've sent a password reset link to {{email}} if an account exists.",
      "instruction1": "• Check your spam/junk folder if you don't see the email",
      "instruction2": "• The reset link expires in 1 hour",
      "instruction3": "• You can request another reset if needed",
      "sendAnother": "Send Another Reset Email",
      "backToLogin": "Back to Login"
    },
    "form": {
      "title": "Forgot Password",
      "description": "Enter your email address and we'll send you a link to reset your password.",
      "emailAddress": "Email Address",
      "placeholder": "Enter your email address",
      "sending": "Sending...",
      "sendResetLink": "Send Reset Link",
      "backToLogin": "Back to Login",
      "noAccount": "Don't have an account?",
      "signUp": "Sign up",
      "lost2FA": "Lost access to your 2FA device?",
      "request2FAReset": "Request 2FA reset"
    },
    "security": {
      "title": "Security Note",
      "note1": "• Password reset is only available for traditional accounts",
      "note2": "• Google OAuth users should use \"Sign in with Google\"",
      "note3": "• Reset links expire after 1 hour for security"
    }
  }
};
